/**
 * THE WAY IN — one question per screen (`client/flow.ts`).
 *
 * The sub-lines are not decoration: each one answers the question the player would otherwise have to ask
 * somebody, and they were written against a specific confusion. Shortening them is a product decision.
 */
export default {
  // ── the menu ──────────────────────────────────────────────────────────────────────────────────────
  menu_title: 'Choose how to play',
  menu_solo: 'New match',
  menu_solo_sub: 'You against the bots — {n} a side.',
  /**
   * THE TWO ROWS MODE 1 WAS MISSING, and the sub-lines are where the difference between them lives: one
   * commits THIS machine to carrying everybody else, the other spends a code somebody read out.
   */
  menu_host: 'Open a match',
  menu_host_sub: 'You host it from this machine and hand out the code. Up to twelve.',
  menu_join: 'Enter with a code',
  menu_join_sub: 'Somebody opened a match and gave you its code.',
  /**
   * WHICH group match this is, which is what the line had never said (§31.1).
   *
   * It sends the field to the ONE session the service runs. That is a real way to play with other people
   * and it is not a room: no code, no invitation and nobody to wait for — and saying so is what stops it
   * being the row somebody looking for a room would press.
   */
  menu_group: 'Group matches',
  menu_group_sub: 'The shared field of this server: you walk in and play with whoever is already there. No room, no code and no invitation.',
  menu_options: 'Options',
  menu_options_sub: 'Sensitivity, volume and display.',
  /** Last of the four, and that is the only decision in it: the three above are ROUTES and this one is not,
   *  so between two of them it would read as one more way to play. */
  menu_signout: 'Sign out',
  menu_signout_sub: 'Leave this account and go back to the door.',
  menu_credits: 'Credits',
  menu_credits_sub: 'Who made this, and what it is made of.',

  // ── the two boxes the menu opens (mode 1) ─────────────────────────────────────────────────────────
  /**
   * THE NAME OF THE MATCH — asked FIRST, because it is the only thing about the room that is nobody
   * else's to decide. The note says the two things that follow and are worth knowing before pressing: the
   * field comes next, and then this machine gets measured. A refusal arriving with no warning that
   * anything was going to be checked reads as a failure rather than as an answer (`hosted.md §7.3`).
   */
  host_label: 'Name of the match',
  host_ph: 'Saturday skirmish',
  host_note: 'It is what the people you invite see while they wait. Next comes the field, and then this machine is checked.',
  host_go: 'Choose the field',
  /**
   * THE CODE, and the box takes the whole link too — which is not a courtesy: the link is what the lobby
   * publishes and therefore what gets copied, so a box that only took the bare code would refuse the exact
   * string this product hands over (`startScreen.ts joinCodeOf`).
   */
  join_label: 'The code',
  join_ph: 'the code, or the whole link',
  join_note: 'You do not choose the field: whoever opened the match already did.',
  join_go: 'Enter',

  // ── the credits ───────────────────────────────────────────────────────────────────────────────────
  /** One entry per role in `CREDIT_ROLES`, in two halves: the heading and who is under it. A role with only
   *  one of the two draws its own key, which is visible rather than silent. */
  credits_title: 'Credits',
  credits_sub: 'A-ARENA is the multiplayer tactical range of A-Airsoft: the same ballistic engine that runs in the phone simulator, resolved by an authoritative service and drawn in the browser.',
  credit_product: 'Product and direction',
  credit_product_who: 'Germán Zelaya',
  credit_code: 'Development',
  credit_code_who: 'Germán Zelaya · Claude (Anthropic)',
  credit_engine: 'Ballistic engine',
  credit_engine_who: 'Shared with the A-Airsoft simulator: drag, hop-up, wind and ricochet. One description, three runtimes.',
  credit_tech: 'Technology',
  credit_tech_who: 'Babylon.js · TypeScript · Node.js · WebSocket · WebRTC',
  credit_art: 'Scenery and models',
  credit_art_who: 'An in-house catalogue, measured and baked for this field, over licensed third-party assets.',
  credits_foot: 'Real ballistics, real tactics. Nothing that decides a hit is drawn twice.',

  // ── the mode ──────────────────────────────────────────────────────────────────────────────────────
  /** This one was MISSING from both catalogues, so the step drew `entry.mode_title` as its own heading:
   *  the fallback working exactly as designed over a key nobody had written. */
  mode_title: 'Choose the mode',

  // ── the field ─────────────────────────────────────────────────────────────────────────────────────
  fields_title: 'Choose the field',
  /** WHAT PICKING COSTS, said before the click and not after it (§169). The wait is real and it is paid
   *  here; a grid that does not name it turns the one long load of the session into a screen that hung. */
  fields_sub: 'Choosing downloads everything the field needs. There is one wait, and it happens here.',
  /** What the card's strip says it is showing. It is a PLAN of the field, not a photograph of it. */
  field_plan: 'plan',
  /**
   * Which option the room is ALREADY running. One key for the field and the mode: it is the same fact
   * about two axes, and a second key would be that fact described twice.
   */
  live: 'in play',
  /** The second half of the two-step that guards a pick which would rearm the room (`§107`). */
  pick_again: 'Tap again to confirm',
  /** The left-handed mirror, in Options with the other knobs about the APPARATUS rather than the game. */
  lefty: `Left-handed`,
  lefty_note: `Mirrors the on-screen controls for a left thumb. The glyphs stay upright.`,
  /** Which field the rest of the way in is being walked on. It rides in the step strip, so every screen
   *  after the pick answers "on which field?" without the player having to step back to look. */

  // ── identity ──────────────────────────────────────────────────────────────────────────────────────
  identity_title: 'Who you are',
  identity_sub: 'Your alias is what everyone else reads on the tape across your back.',
  identity_alias: 'Alias',
  // The label when the gate NAMED this browser: the field above it is a readout and not an
  // input, so calling a real callsign "alias" would be the screen contradicting what it shows.
  identity_callsign: 'Callsign',
  avatar_title: 'Your body',
  avatar_sub: 'Looks only: every body measures the same for the shot.',
  identity_random: 'Whoever I get',
  /** "SIDE" and not "team": the identity readout three lines above this already draws the person's
   *  TEAM, which is the squad the coordination plane knows about, and which end of the field he stands at
   *  is another object entirely. See the Spanish catalogue for the whole argument. */
  side_label: 'Side',
  side_auto: 'Whichever I get',
  identity_operator_m: 'Operator',
  identity_operator_f: 'Operator',

  // ── group ─────────────────────────────────────────────────────────────────────────────────────────
  group_title: 'Your group',
  group_sub: 'The patch is sewn to your back and sleeve. It is identity, not side: it does not say which team you are on and changes nothing that is scored.',
  group_none: 'No group',

  // ── the marcadora ─────────────────────────────────────────────────────────────────────────────────
  marker_title: 'Your marcadora',
  marker_sub: 'The model you want in your hands. Nearly all of them are a look: the seal — which decides the action, the minimum range and the magazine — is chosen on the next step. The bolt action is the exception, and its row says so: choosing it IS choosing its class.',
  marker_side: "My side's",
  marker_side_sub: 'Whatever the field issues your side. It changes if you change sides.',
  /** The heading of the panel that reads back what the reel has landed on. One key for both reels. */
  chosen: 'Chosen',

  // ── the seal ──────────────────────────────────────────────────────────────────────────────────────
  seal_title: 'Your seal',
  seal_sub: 'Optional: it is the energy CEILING you enter with, and it decides the fire mode, the magazine and the minimum range. Choose nothing and you enter with whatever the field issues. Within the ceiling, velocity, BB weight and hop are dialled in with L.',
  seal_field: "The field's",
  seal_field_sub: 'The default seal for this room.',
  seal_min: 'minimum {n} m',
  seal_nomin: 'no minimum',

  // ── the viewer's readout ──────────────────────────────────────────────────────────────────────────
  // What has been answered so far, under the body being built. Three cells and not a sentence: they are
  // read at a glance, between one pick and the next.
  readout_alias: 'ALIAS',
  readout_callsign: 'CALLSIGN',
  readout_group: 'GROUP',
  readout_hands: 'IN HAND',
  alias_ph: 'your alias',

  // ── options and pause ─────────────────────────────────────────────────────────────────────────────
  tune_title: 'Fine tuning',
  tune_sub: 'Optional: within your seal ceiling you can move velocity, BB weight and hop. It is the same panel L opens in the field, and it changes neither the action nor the minimum range.',
  options_title: 'Options',
  options_back: 'Back to the field',
  controls_hint: 'The controls are shown with {key}, drawn on a keyboard — inside the field and without leaving the match.',
  preview_hint: 'drag to turn · wheel to zoom',
  rearm_note: 'Rearms the session: everyone playing reloads.',
  seal_fixed_note: 'Within that ceiling, velocity, BB weight and hop are dialled in the field with L.',
  options_language: 'Language',
  options_sensitivity: 'Mouse sensitivity',
  options_volume: 'Volume',
  options_bots: 'Bots a side',
  options_bots_less: 'fewer bots',
  options_bots_more: 'more bots',
  options_bots_note: 'How many enemies and team-mates a new match brings.',
  options_image: 'Image',
  options_image_note: 'How the field is developed. Dialled on one screen — move it until it reads right on yours.',
  options_image_reset: "Back to the field's own",
  options_image_tone: 'Tone curve',
  options_tone_off: 'None',
  options_tone_standard: 'Standard',
  options_tone_aces: 'ACES',
  options_tone_neutral: 'Neutral',
  options_image_exposure: 'Brightness',
  options_image_contrast: 'Contrast',
  options_image_env: 'Reflections',
  options_image_env_note: 'Lights surfaces with the sky itself. Off costs less on a modest machine.',
  options_image_strength: 'Reflection strength',
  options_image_strength_off: 'It only does anything with reflections on.',
  fullscreen: 'Full screen on entry',
  fullscreen_blocks: 'It also blocks CTRL+W, which in an ordinary tab closes the game without asking.',
  fullscreen_cannot: 'This browser will not let CTRL+W be blocked — it asks for confirmation on close anyway.',
  pause_title: 'Paused',
  pause_sub: 'The game is still running.',
  pause_watch: 'Watch the field',
  pause_back: 'Back to the field',
  /** The row needs a second line because every row on this surface has one, and a lone bare row reads as a
   *  different KIND of control — which is the distinction §169 removed. */
  pause_back_sub: 'Take the controls again. The match never stopped running.',
  pause_change_field: 'Change field',
  pause_leave: 'Leave the match',
  pause_leave_sub: 'Drops your body and closes the link. You return to the entry screen.',
  // The pause the player did not ask for: magazines and cans both gone, which is the one state a match
  // cannot come back from. It says the match is over rather than leaving a trigger that answers nothing.
  spent_title: 'OUT OF AMMUNITION',
  spent_sub: 'You are out of magazines and smoke: your match is over. Start a new one from here.',

  // The alias, confirmed. The authority is the only one that can make the rest of the field see it, so
  // what is said back is what it accepted rather than what was typed.
  alias_set: 'you enter as {name}',

  // ── shared controls ───────────────────────────────────────────────────────────────────────────────
  next: 'Continue',
  enter: 'Enter the field',
  back: '◄ Back',
  mode_skirmish: `Skirmish`,
  mode_skirmish_sub: `One hit takes you out of the round. The side left standing wins, or the one with more alive when the clock runs out.`,
  mode_ctf: `Capture the Flag`,
  mode_ctf_sub: `Carry the other side’s flag home. With the flag in hand only the sidearm works.`,
};
